import * as stylex from '@stylexjs/stylex';

const pulse = stylex.keyframes({
    '0%, 100%': {
        transform: 'scale(1)', opacity: 1
    }

    ,
    '50%': {
        transform: 'scale(1.05)', opacity: 0.8
    }

    ,
});

const MOBILE = '@media (max-width: 768px)';
const SMALL_MOBILE = '@media (max-width: 480px)';

export const styles = stylex.create({
    faqPage: {
        maxWidth: 'var(--max-width)',
        marginInline: 'auto',
        paddingInline: 'var(--spacing-md)',
        color: 'var(--text-primary)',
        scrollBehavior: 'smooth',
    }

    ,

    /* Back Link */
    backLink: {

        display: 'inline-flex',
        alignItems: 'center',
        gap: 'var(--spacing-sm)',
        color: {
            default: 'var(--text-secondary)',
            ':hover': 'var(--primary)',
        }

        ,
        textDecoration: 'none',
        fontSize: 'var(--font-size-sm)',
        marginBottom: 'var(--spacing-md)',
        transitionProperty: 'color',
        transitionDuration: 'var(--transition-fast)',
    }

    ,

    /* Hero Section */
    heroSection: {

        backgroundImage: `linear-gradient(135deg,
            var(--bg-card) 0%,
            rgba(59, 130, 246, 0.02) 15%,
            rgba(16, 185, 129, 0.02) 35%,
            rgba(255, 107, 107, 0.02) 55%,
            var(--bg-card) 100%)`,
        borderRadius: 'var(--radius-xl)',
        padding: {
            default: 'var(--spacing-3xl)',
            [MOBILE]: 'var(--spacing-xl)',
            [SMALL_MOBILE]: 'var(--spacing-lg)',
        }

        ,
        marginBottom: 'var(--spacing-3xl)',
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden',
        backdropFilter: 'blur(4px)',
        '::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            backgroundImage: `radial-gradient(circle at 20% 30%, rgba(59, 130, 246, 0.03) 0%, transparent 50%),
            radial-gradient(circle at 80% 70%, rgba(16, 185, 129, 0.03) 0%, transparent 50%)`,
            opacity: 1,
        }

        ,
    }

    ,
    heroContent: {
        position: 'relative',
        zIndex: 2,
    }

    ,
    heroIcon: {
        marginBottom: 'var(--spacing-lg)',
        color: 'var(--accent)',
        animationName: pulse,
        animationDuration: '2s',
        animationTimingFunction: 'ease-in-out',
        animationIterationCount: 'infinite',
    }

    ,
    heroTitle: {
        fontSize: {
            default: '2.5rem',
            [MOBILE]: '2rem',
            [SMALL_MOBILE]: '1.75rem',
        }

        ,
        fontWeight: 700,
        margin: '0 0 var(--spacing-lg) 0',
        color: 'var(--text-primary)',
        lineHeight: 1.2,
    }

    ,
    heroDescription: {
        fontSize: 'var(--font-size-lg)',
        color: 'var(--text-secondary)',
        lineHeight: 1.6,
        marginBottom: 'var(--spacing-2xl)',
        maxWidth: '600px',
        marginInline: 'auto',
    }

    ,
    heroStats: {

        display: 'flex',
        justifyContent: 'center',
        gap: 'var(--spacing-xl)',
        flexWrap: 'wrap',
        flexDirection: {
            [MOBILE]: 'column'
        }

        ,
    }

    ,
    stat: {
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--spacing-sm)',
        color: 'var(--text-secondary)',
        fontSize: 'var(--font-size-sm)',
        fontWeight: 500,
    }

    ,

    /* Search Bar */
    searchSection: {
        marginBottom: 'var(--spacing-3xl)',
    }

    ,
    searchContainer: {
        marginBottom: 'var(--spacing-xl)',
    }

    ,
    searchInputWrapper: {
        position: 'relative',
        maxWidth: '600px',
        marginInline: 'auto',
    }

    ,
    searchIcon: {
        position: 'absolute',
        left: 'var(--spacing-md)',
        top: '50%',
        transform: 'translateY(-50%)',
        color: 'var(--text-secondary)',
        zIndex: 1,
    }

    ,
    searchInput: {

        width: '100%',
        paddingTop: 'var(--spacing-md)',
        paddingBottom: 'var(--spacing-md)',
        paddingRight: 'var(--spacing-md)',
        paddingLeft: 'calc(var(--spacing-md) * 2 + 20px)',
        borderWidth: '2px',
        borderStyle: 'solid',
        borderColor: {
            default: 'var(--border)',
            ':focus': 'var(--primary)',
        }

        ,
        borderRadius: 'var(--radius-full)',
        fontSize: 'var(--font-size-md)',
        backgroundColor: 'var(--bg-primary)',
        color: 'var(--text-primary)',
        transitionProperty: 'all',
        transitionDuration: 'var(--transition-normal)',
        outline: {
            ':focus': 'none'
        }

        ,
        boxShadow: {
            ':focus': '0 0 0 3px rgba(var(--primary-rgb), 0.1)'
        }

        ,
    }

    ,
    searchResults: {
        textAlign: 'center',
        marginTop: 'var(--spacing-sm)',
        color: 'var(--text-secondary)',
        fontSize: 'var(--font-size-sm)',
    }

    ,

    /* FAQ List */
    faqList: {
        marginBottom: 'var(--spacing-3xl)',
    }

    ,
    faqContainer: {
        maxWidth: '800px',
        marginInline: 'auto',
    }

    ,
    faqItem: {

        backgroundColor: 'var(--bg-surface)',
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: {
            default: 'var(--border)',
            ':hover': 'var(--accent)',
        }

        ,
        borderRadius: 'var(--radius-lg)',
        marginBottom: 'var(--spacing-md)',
        overflow: 'hidden',
        transitionProperty: 'all',
        transitionDuration: 'var(--transition-normal)',
        boxShadow: {
            ':hover': 'var(--shadow-md)'
        }

        ,
    }

    ,
    faqQuestion: {

        width: '100%',
        backgroundColor: {
            default: 'transparent',
            ':hover': 'var(--bg-hover)',
        }

        ,
        borderWidth: 0,
        padding: {
            default: 'var(--spacing-xl)',
            [MOBILE]: 'var(--spacing-lg)',
            [SMALL_MOBILE]: 'var(--spacing-md)',
        }

        ,
        textAlign: 'left',
        cursor: 'pointer',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        color: 'var(--text-primary)',
        fontSize: {
            default: 'var(--font-size-lg)',
            [MOBILE]: 'var(--font-size-md)',
            [SMALL_MOBILE]: 'var(--font-size-sm)',
        }

        ,
        fontWeight: 600,
        transitionProperty: 'all',
        transitionDuration: 'var(--transition-fast)',
        backdropFilter: 'blur(4px)',
    }

    ,
    faqQuestionText: {
        flex: 1,
        marginRight: 'var(--spacing-md)',
    }

    ,
    faqChevron: {
        color: 'var(--accent)',
        flexShrink: 0,
        transitionProperty: 'transform',
        transitionDuration: 'var(--transition-fast)',
    }

    ,
    faqAnswer: {
        paddingTop: {
            default: 'var(--spacing-xl)',
            [MOBILE]: 0,
        }

        ,
        paddingBottom: {
            default: 'var(--spacing-xl)',
            [MOBILE]: 'var(--spacing-lg)',
            [SMALL_MOBILE]: 'var(--spacing-md)',
        }

        ,
        paddingInline: {
            default: 'var(--spacing-xl)',
            [MOBILE]: 'var(--spacing-lg)',
            [SMALL_MOBILE]: 'var(--spacing-md)',
        }

        ,
        borderTopWidth: '1px',
        borderTopStyle: 'solid',
        borderTopColor: 'var(--border)',
        backgroundColor: 'var(--bg-secondary)',
    }

    ,
    faqAnswerText: {
        color: 'var(--text-secondary)',
        lineHeight: 1.6,
        margin: '0 0 var(--spacing-md) 0',
    }

    ,

    /* Support CTA */
    supportCta: {
        backgroundImage: `linear-gradient(135deg,
            var(--bg-card) 0%,
            rgba(59, 130, 246, 0.02) 25%,
            rgba(16, 185, 129, 0.02) 75%,
            var(--bg-card) 100%)`,
        borderRadius: 'var(--radius-xl)',
        padding: 'var(--spacing-3xl)',
        textAlign: 'center',
        marginBottom: 'var(--spacing-3xl)',
        backdropFilter: 'blur(4px)',
    }

    ,
    ctaTitle: {
        fontSize: 'var(--font-size-2xl)',
        fontWeight: 600,
        margin: '0 0 var(--spacing-md) 0',
        color: 'var(--text-primary)',
    }

    ,
    ctaDescription: {
        fontSize: 'var(--font-size-lg)',
        color: 'var(--text-secondary)',
        margin: '0 0 var(--spacing-xl) 0',
        maxWidth: '500px',
        marginInline: 'auto',
    }

    ,
    ctaButton: {

        display: 'inline-flex',
        alignItems: 'center',
        gap: 'var(--spacing-sm)',
        backgroundColor: {
            default: 'var(--accent)',
            ':hover': 'var(--accent-hover)',
        }

        ,
        color: 'var(--text-on-accent)',
        paddingBlock: 'var(--spacing-md)',
        paddingInline: 'var(--spacing-xl)',
        borderRadius: 'var(--radius-full)',
        textDecoration: 'none',
        fontWeight: 600,
        fontSize: 'var(--font-size-lg)',
        transitionProperty: 'all',
        transitionDuration: 'var(--transition-normal)',
        boxShadow: {
            default: '0 4px 12px rgba(0, 0, 0, 0.15)',
            ':hover': '0 6px 20px rgba(0, 0, 0, 0.2)',
        }

        ,
        transform: {
            ':hover': 'translateY(-2px)'
        }

        ,
    }

    ,
});